import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ConfirmModal, StateView, type MenuAction } from '@/card/record'
import { formatNumber } from '@/lib/format'
import { AdminState, EditLayer, Field, LayerButtons, Notice, RowMenu, SearchBox, SkeletonTable, Switch } from '@/admin/AdminShell'
import type { CellFilter, CellLimits, CellRow, ZoneRow } from './topologyApi'
import { blockCells, deleteCell, fetchCells, unblockCells, updateCell } from './topologyApi'
import { WAREHOUSES_KEY } from './WarehousesPage'
import { CellGenerator } from './CellGenerator'

type Tri = 'all' | 'yes' | 'no'

/**
 * Ячейки зоны: фильтры, правка ёмкости, массовая блокировка, удаление и генератор.
 *
 * **Ячейку с товаром, резервом или открытым заданием удалить нельзя** — только
 * заблокировать. Пункт «Удалить» у такой ячейки виден, но недоступен с причиной словами:
 * спрятанный пункт выглядел бы как отсутствие возможности, а не как запрет с поводом.
 * Причину знает сервер (`hasStock`, `hasReservation`, `hasOpenTask`) и всё равно откажет
 * сам (`topology.cell_in_use`), если за время между загрузкой и нажатием на ячейку положили
 * товар.
 *
 * Сервер отдаёт не больше 1000 ячеек и говорит, сколько их всего: зона на пять тысяч
 * ячеек сужается рядом, а не прокручивается.
 *
 * Что можно делать, решает склад (`editable`/`deletable`/`extendable` с сервера): у чужого
 * склада заведующий видит ячейки, но не правит их.
 */
export function CellsPanel({ zone, maxCells, defaultMask, mayCreate, mayEdit, mayDelete, onChanged }: {
  zone: ZoneRow
  maxCells: number
  defaultMask: string
  mayCreate: boolean
  mayEdit: boolean
  mayDelete: boolean
  onChanged: () => void
}) {
  const { t, locale } = useApp()
  const queryClient = useQueryClient()
  const [row, setRow] = useState('')
  const [blocked, setBlocked] = useState<Tri>('all')
  const [occupied, setOccupied] = useState<Tri>('all')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [generating, setGenerating] = useState(false)
  const [editing, setEditing] = useState<CellRow | null>(null)
  const [removing, setRemoving] = useState<CellRow | null>(null)
  const [blocking, setBlocking] = useState(false)
  const [reason, setReason] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const filter: CellFilter = {
    row: row || undefined,
    blocked: blocked === 'all' ? undefined : blocked === 'yes',
    occupied: occupied === 'all' ? undefined : occupied === 'yes',
    q: query.trim() || undefined,
  }
  const key = [...WAREHOUSES_KEY, 'cells', zone.id, filter]
  const page = useQuery({ queryKey: key, queryFn: ({ signal }) => fetchCells(zone.id, filter, signal) })
  const cells = page.data?.cells ?? []

  const refresh = () => {
    setSelected(new Set())
    void queryClient.invalidateQueries({ queryKey: [...WAREHOUSES_KEY, 'cells', zone.id] })
    onChanged()
  }
  const fail = (error: unknown) => setNotice(error instanceof Error ? error.message : t('app.error'))

  const block = useMutation({
    mutationFn: () => blockCells(zone.id, [...selected], reason.trim()),
    onSuccess: () => { setBlocking(false); setReason(''); refresh() },
    onError: (error) => { setBlocking(false); fail(error) },
  })
  const unblock = useMutation({
    mutationFn: () => unblockCells(zone.id, [...selected]),
    onSuccess: refresh,
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: (cell: CellRow) => deleteCell(cell.id),
    onSuccess: () => { setRemoving(null); refresh() },
    onError: (error) => { setRemoving(null); fail(error) },
  })

  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }
  const allSelected = cells.length > 0 && cells.every((cell) => selected.has(cell.id))

  return (
    <section className="panel" aria-labelledby="topo-cells-h">
      <div className="panel-h">
        <span className="h2" id="topo-cells-h">{t('topo.cells.title', { zone: zone.code })}</span>
        {page.data && (
          <span className="count">
            {page.data.truncated
              ? t('topo.cells.truncated', { shown: cells.length, total: page.data.total })
              : formatNumber(page.data.total, locale)}
          </span>
        )}
        <span className="spacer" />
        {mayCreate && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setGenerating(true)}>
            <Icon name="plus" size="sm" />{t('topo.gen.open')}
          </button>
        )}
      </div>
      <div className="toolbar" style={{ padding: '8px 12px', flexWrap: 'wrap', gap: 8 }}>
        <SearchBox value={query} onChange={setQuery} label={t('topo.cells.search')} width={180} />
        <label className="row" style={{ gap: 6 }}>
          <span className="t-sm muted">{t('topo.cells.row')}</span>
          <select className="input input-sm" aria-label={t('topo.cells.row')} value={row} onChange={(e) => setRow(e.target.value)}>
            <option value="">{t('topo.cells.anyRow')}</option>
            {(page.data?.rows ?? []).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <TriSwitch label={t('topo.cells.blockedFilter')} value={blocked} onChange={setBlocked} yes={t('topo.cells.blockedYes')} no={t('topo.cells.blockedNo')} />
        <TriSwitch label={t('topo.cells.occupiedFilter')} value={occupied} onChange={setOccupied} yes={t('topo.cells.occupiedYes')} no={t('topo.cells.occupiedNo')} />
      </div>
      {mayEdit && selected.size > 0 && (
        <div className="bulk-bar" role="region" aria-label={t('topo.cells.bulk')}>
          <span>{t('topo.cells.selected', { count: selected.size })}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBlocking(true)}>
            <Icon name="lock" size="sm" />{t('topo.cells.block')}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={unblock.isPending} onClick={() => unblock.mutate()}>
            {t('topo.cells.unblock')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>{t('topo.cells.clear')}</button>
        </div>
      )}
      <Notice text={notice} />
      <AdminState isLoading={page.isLoading} error={page.error} onRetry={() => void page.refetch()}
                  forbiddenTitleKey="topo.forbidden" errorTitleKey="topo.error" skeleton={<SkeletonTable columns={7} rows={6} />}>
        {cells.length === 0 ? (
          zone.cells === 0 ? (
            <StateView kind="setup" compact title={t('topo.cells.setup')} description={t('topo.cells.setup.text')} />
          ) : (
            <StateView kind="empty" compact title={t('topo.cells.empty')} />
          )
        ) : (
          <div className="tbl-wrap">
            <table className="tbl" aria-label={t('topo.cells.title', { zone: zone.code })}>
              <thead>
                <tr>
                  {mayEdit && (
                    <th style={{ width: 32 }}>
                      <input type="checkbox" aria-label={t('topo.cells.selectAll')} checked={allSelected}
                             onChange={() => setSelected(allSelected ? new Set() : new Set(cells.map((cell) => cell.id)))} />
                    </th>
                  )}
                  <th>{t('topo.cells.code')}</th>
                  <th>{t('topo.cells.address')}</th>
                  <th>{t('topo.cells.capacity')}</th>
                  <th>{t('topo.cells.dims')}</th>
                  <th>{t('topo.cells.abc')}</th>
                  <th>{t('topo.cells.state')}</th>
                  {(mayEdit || mayDelete) && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                </tr>
              </thead>
              <tbody>
                {cells.map((cell) => {
                  const busy = busyReason(cell, t)
                  const items: MenuAction[] = []
                  if (mayEdit) items.push({ id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setEditing(cell) })
                  if (mayDelete) items.push({
                    id: 'delete', label: t('topo.cells.delete'), icon: 'trash', danger: true,
                    onSelect: () => setRemoving(cell), disabledReason: busy ?? undefined,
                  })
                  return (
                    <tr key={cell.id} className={selected.has(cell.id) ? 'is-selected' : undefined}>
                      {mayEdit && (
                        <td>
                          <input type="checkbox" aria-label={t('topo.cells.selectOne', { code: cell.code })}
                                 checked={selected.has(cell.id)} onChange={() => toggle(cell.id)} />
                        </td>
                      )}
                      <td><span className="mono strong">{cell.code}</span></td>
                      <td className="t-sm">
                        {[cell.rowNo, cell.rackNo, cell.tierNo, cell.positionNo].map((v) => v ?? '·').join(' / ')}
                      </td>
                      <td className="t-sm">{capacity(cell, t, locale)}</td>
                      <td className="t-sm mono">
                        {cell.lengthMm || cell.widthMm || cell.heightMm
                          ? `${cell.lengthMm ?? '—'}×${cell.widthMm ?? '—'}×${cell.heightMm ?? '—'}`
                          : <span className="muted">{t('topo.cells.unlimited')}</span>}
                      </td>
                      <td>{cell.abcClass ?? <span className="muted">—</span>}</td>
                      <td>
                        <span className="row-wrap" style={{ gap: 4 }}>
                          {cell.blocked && <span className="badge b-danger" title={cell.blockReason}>{t('topo.cells.blockedBadge')}</span>}
                          {!cell.pickable && <span className="badge">{t('topo.cells.reserveBadge')}</span>}
                          {cell.hasStock && <span className="badge b-info">{t('topo.cells.stockBadge')}</span>}
                          {cell.hasReservation && <span className="badge b-warning">{t('topo.cells.reservedBadge')}</span>}
                          {cell.hasOpenTask && <span className="badge b-warning">{t('topo.cells.taskBadge')}</span>}
                          {!cell.blocked && cell.pickable && !cell.hasStock && !cell.hasReservation && !cell.hasOpenTask && (
                            <span className="muted t-sm">{t('topo.cells.free')}</span>
                          )}
                        </span>
                        {cell.blocked && cell.blockReason && <div className="t-xs muted">{cell.blockReason}</div>}
                      </td>
                      {(mayEdit || mayDelete) && (
                        <td className="adm-act"><RowMenu label={t('topo.cells.actions', { code: cell.code })} items={items} /></td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminState>

      {generating && (
        <CellGenerator zone={zone} maxCells={maxCells} defaultMask={defaultMask}
                       onClose={() => setGenerating(false)} onCreated={refresh} />
      )}
      {editing && <CellSheet cell={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
      <ConfirmModal
        open={removing != null}
        title={t('topo.cells.deleteConfirm', { code: removing?.code ?? '' })}
        confirmLabel={t('topo.cells.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
      >
        <p>{t('topo.cells.deleteConfirm.text')}</p>
      </ConfirmModal>
      <ConfirmModal
        open={blocking}
        title={t('topo.cells.blockConfirm', { count: selected.size })}
        confirmLabel={t('topo.cells.block')}
        icon="lock"
        tone="warning"
        pending={block.isPending}
        confirmDisabled={reason.trim() === ''}
        onCancel={() => setBlocking(false)}
        onConfirm={() => block.mutate()}
      >
        <Field label={t('topo.cells.reason')} htmlFor="cell-block-reason" required help={t('topo.cells.reason.help')}>
          <input id="cell-block-reason" className="input" maxLength={255} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </ConfirmModal>
    </section>
  )
}

function TriSwitch({ label, value, onChange, yes, no }: {
  label: string
  value: Tri
  onChange: (value: Tri) => void
  yes: string
  no: string
}) {
  const { t } = useApp()
  return (
    <div className="seg" role="group" aria-label={label}>
      {(['all', 'yes', 'no'] as const).map((option) => (
        <button key={option} type="button" aria-pressed={value === option} onClick={() => onChange(option)}>
          {option === 'all' ? t('topo.cells.any') : option === 'yes' ? yes : no}
        </button>
      ))}
    </div>
  )
}

/** Почему ячейку нельзя удалить — словами, или null, если можно. */
export function busyReason(cell: CellRow, t: (key: string) => string): string | null {
  const reasons = [
    cell.hasStock ? t('topo.cells.busy.stock') : null,
    cell.hasReservation ? t('topo.cells.busy.reserved') : null,
    cell.hasOpenTask ? t('topo.cells.busy.task') : null,
  ].filter(Boolean)
  return reasons.length ? `${reasons.join(', ')} — ${t('topo.cells.busy.blockInstead')}` : null
}

function capacity(cell: CellRow, t: (key: string, params?: Record<string, string | number>) => string,
                  locale: Parameters<typeof formatNumber>[1]) {
  const parts = [
    cell.maxWeightKg !== undefined ? t('topo.cells.kg', { value: formatNumber(cell.maxWeightKg, locale) }) : null,
    cell.maxVolumeM3 !== undefined ? t('topo.cells.m3', { value: formatNumber(cell.maxVolumeM3, locale) }) : null,
    cell.maxPallets !== undefined ? t('topo.cells.pallets', { value: cell.maxPallets }) : null,
  ].filter(Boolean)
  // Незаданный лимит — «без ограничения», а не ноль: так его и понимает подсказка размещения.
  return parts.length ? parts.join(' · ') : <span className="muted">{t('topo.cells.unlimited')}</span>
}

const num = (value: string) => (value.trim() === '' ? null : Number(value.replace(',', '.')))
const text = (value: number | undefined) => (value === undefined ? '' : String(value))

/** Правка ёмкости, габаритов, класса ABC и признака отбора одной ячейки. */
function CellSheet({ cell, onClose, onSaved }: { cell: CellRow; onClose: () => void; onSaved: () => void }) {
  const { t } = useApp()
  const formId = useId()
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    maxWeightKg: text(cell.maxWeightKg), maxVolumeM3: text(cell.maxVolumeM3), maxPallets: text(cell.maxPallets),
    lengthMm: text(cell.lengthMm), widthMm: text(cell.widthMm), heightMm: text(cell.heightMm),
    abcClass: cell.abcClass ?? '', pickable: cell.pickable,
  })
  const values = [draft.maxWeightKg, draft.maxVolumeM3, draft.maxPallets, draft.lengthMm, draft.widthMm, draft.heightMm].map(num)
  const invalid = values.some((value) => value !== null && (Number.isNaN(value) || value <= 0))

  const save = useMutation({
    mutationFn: () => {
      const body: CellLimits & { version: number } = {
        maxWeightKg: values[0] ?? null, maxVolumeM3: values[1] ?? null, maxPallets: values[2] ?? null,
        lengthMm: values[3] ?? null, widthMm: values[4] ?? null, heightMm: values[5] ?? null,
        abcClass: draft.abcClass || null, pickable: draft.pickable, version: cell.version,
      }
      return updateCell(cell.id, body)
    },
    onSuccess: () => { onSaved(); onClose() },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  const input = (key: keyof typeof draft, label: string, id: string) => (
    <Field label={label} htmlFor={id}>
      <input id={id} className="input mono" inputMode="decimal" value={String(draft[key])}
             onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
    </Field>
  )

  return (
    <EditLayer kind="sheet" overline={t('topo.cells.form')} title={cell.code} subtitle={cell.barcode} onClose={onClose}
               footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending} disabled={invalid} form={formId} />}>
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('topo.cells.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <p className="t-sm muted">{t('topo.cells.limits.help')}</p>
        <div className="form-grid">
          {input('maxWeightKg', t('topo.cells.maxWeight'), 'cell-weight')}
          {input('maxVolumeM3', t('topo.cells.maxVolume'), 'cell-volume')}
        </div>
        <div className="form-grid">
          {input('maxPallets', t('topo.cells.maxPallets'), 'cell-pallets')}
          <Field label={t('topo.cells.abc')} htmlFor="cell-abc" help={t('topo.cells.abc.help')}>
            <select id="cell-abc" className="input" value={draft.abcClass} onChange={(e) => setDraft({ ...draft, abcClass: e.target.value })}>
              <option value="">—</option>
              {['A', 'B', 'C'].map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </Field>
        </div>
        <div className="form-grid">
          {input('lengthMm', t('topo.cells.length'), 'cell-length')}
          {input('widthMm', t('topo.cells.width'), 'cell-width')}
        </div>
        <div className="form-grid">
          {input('heightMm', t('topo.cells.height'), 'cell-height')}
        </div>
        {invalid && <Notice text={t('topo.cells.limitInvalid')} />}
        <Field label={t('topo.cells.pickable')} help={t('topo.cells.pickable.help')}>
          <Switch checked={draft.pickable} ariaLabel={t('topo.cells.pickable')} onChange={(value) => setDraft({ ...draft, pickable: value })} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
